/**
 * The buttons on a member row: edit, till PIN, remove.
 *
 * One component for every layout that lists members — My's table, My's phone
 * cards, the Team panel. The phone cards were a hand-copied second set of
 * these buttons and quietly lacked the PIN one; rendering every layout from
 * this makes that class of gap impossible rather than merely fixed.
 *
 * Each action is optional: a caller that cannot edit or remove passes nothing
 * and the button is not drawn. The backend enforces the same permissions.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { BadgePercent, KeyRound, Pencil, Trash2 } from 'lucide-react'
import { Button } from '../ui/button'
import { ORG_WIDE_ROLES, canDiscount, teamApi, type TeamMember } from './teamModel'

export default function MemberActions({
  member, onEdit, onPin, onRemove, removeDisabledReason, onDiscountChanged,
}: {
  member: TeamMember
  onEdit?: () => void
  onPin?: () => void
  onRemove?: () => void
  /** Draws the manual-discount switch; called with the updated member. */
  onDiscountChanged?: (member: TeamMember) => void
  /** Set when removal is not allowed for this row (e.g. yourself). */
  removeDisabledReason?: string
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const allowed = canDiscount(member)
  const always = ORG_WIDE_ROLES.has(member.role)
  const discountLabel = always
    ? t('users.discountAlways', 'Owners and admins can always give discounts')
    : allowed
      ? t('users.discountOn', 'Can give manual discounts at the till — tap to stop')
      : t('users.discountOff', 'Cannot give manual discounts — tap to allow')
  const toggleDiscount = async () => {
    if (always || !onDiscountChanged) return
    setBusy(true)
    try {
      await teamApi.setDiscount(member.id, !member.pos_manual_discount)
      onDiscountChanged({ ...member, pos_manual_discount: !member.pos_manual_discount })
      toast.success(!member.pos_manual_discount
        ? t('users.discountGranted', '{{name}} can now give discounts.', { name: member.name })
        : t('users.discountRevoked', '{{name}} can no longer give discounts.', { name: member.name }))
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? t('users.discountFailed', 'Could not change that.'))
    } finally { setBusy(false) }
  }

  const pinLabel = member.has_pos_pin
    ? t('users.pinAssigned', 'Till PIN assigned — tap to change')
    : t('users.pinNotAssigned', 'No till PIN — tap to assign')

  return (
    <div className="flex shrink-0 gap-1">
      {onEdit && (
        <Button variant="ghost" size="icon" onClick={onEdit}
                aria-label={t('common.edit', 'Edit')} title={t('common.edit', 'Edit')}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      {onPin && (
        <Button
          variant="ghost" size="icon"
          className={member.has_pos_pin ? 'text-foreground' : 'text-muted-foreground'}
          title={pinLabel} aria-label={pinLabel}
          onClick={onPin}
          data-testid="member-pin"
        >
          <KeyRound className="h-3.5 w-3.5" />
        </Button>
      )}
      {onDiscountChanged && (
        <Button
          variant="ghost" size="icon"
          className={allowed ? 'text-success-fg' : 'text-muted-foreground'}
          title={discountLabel} aria-label={discountLabel} aria-pressed={allowed}
          disabled={busy || always}
          onClick={() => void toggleDiscount()}
          data-testid="member-discount"
        >
          <BadgePercent className="h-3.5 w-3.5" />
        </Button>
      )}
      {onRemove && (
        <Button
          variant="ghost" size="icon" className="text-destructive disabled:text-muted-foreground"
          disabled={!!removeDisabledReason}
          title={removeDisabledReason ?? t('common.delete', 'Remove')}
          aria-label={t('common.delete', 'Remove')}
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}
